import { faker } from "@faker-js/faker";
import { AssetType, CompanyType, EndpointType } from "@/graphql/__generated__/graphql-operations";
import { TCreateCompanySchema } from "@/models";

const makeVatNumber = () => {
  const prefix = faker.string.alpha({ length: 2, casing: "upper" });
  const digits = faker.string.numeric(9);
  return `${prefix}${digits}`;
};

const makePhoneNumber = () => `+${faker.string.numeric(11)}`;

const makeIban = () => {
  const country = faker.string.alpha({ length: 2, casing: "upper" });
  const checkDigits = faker.string.numeric(2);
  const bank = faker.string.alphanumeric(4).toUpperCase();
  const accountStart = faker.string.numeric(7);
  const accountTailLength = faker.number.int({ min: 0, max: 16 });
  const accountTail = faker.string.alphanumeric(accountTailLength).toUpperCase();
  return `${country}${checkDigits}${bank}${accountStart}${accountTail}`;
};

const makeBic = () => faker.finance.bic().toUpperCase();

const makeEndpointType = () => {
  const endpointTypes = Object.values(EndpointType) as EndpointType[];
  return faker.helpers.arrayElement(endpointTypes);
};

const buildBaseCompany = (companytype: CompanyType): TCreateCompanySchema => ({
  companytype,
  name: faker.company.name(),
  phone: makePhoneNumber(),
  vatnumber: makeVatNumber(),
  address: {
    street: faker.location.street(),
    housenumber: String(faker.number.int({ min: 1, max: 999 })),
    zipcode: faker.location.zipCode(),
    city: faker.location.city(),
    country: faker.location.country(),
  },
  bankaccount: {
    beneficiaryID: faker.string.uuid(),
    iban: makeIban(),
    bic: makeBic(),
    name: faker.company.name(),
  },
  serviceproviderinput: undefined,
});

export const createFinancierMockData = (): TCreateCompanySchema => buildBaseCompany(CompanyType.Financier);

export const createServiceProviderMockData = (): TCreateCompanySchema => {
  const baseCompany = buildBaseCompany(CompanyType.Serviceprovider);
  const payoutPeriod = 30;
  const daysLocked = 90;
  const floor = 100;
  const ceiling = 250;

  return {
    ...baseCompany,
    serviceproviderinput: {
      assetdocumentary: {
        endpoint: faker.internet.url(),
        authendpoint: faker.internet.url(),
        username: faker.internet.username(),
        password: faker.internet.password({ length: 12 }),
        endpointType: makeEndpointType(),
      },
      assetfinancingpool: {
        assetType: AssetType.Inventory,
        contractCID: faker.string.alphanumeric(12),
        uniqueitems: true,
        ceiling,
        floor,
        financinglimit: faker.number.int({ min: 10000, max: 250000 }),
        interest: faker.number.int({ min: 10, max: 500 }),
        rewardpayoutperiod: payoutPeriod,
        dayslocked: daysLocked,
      },
      invoicedocumentary: {
        endpoint: faker.internet.url(),
        authendpoint: faker.internet.url(),
        username: faker.internet.username(),
        password: faker.internet.password({ length: 12 }),
        endpointType: makeEndpointType(),
      },
      companydetails: {
        industry: faker.commerce.department(),
        description: faker.company.catchPhrase(),
        yearlyrevenue: faker.number.int({ min: 100000, max: 10000000 }),
        foundingyear: faker.number.int({ min: 1900, max: new Date().getFullYear() }),
        website: `https://www.${faker.internet.domainName()}`,
        facebook: `https://www.facebook.com/${faker.internet.username()}`,
        twitter: `https://www.twitter.com/${faker.internet.username()}`,
        linkedin: `https://www.linkedin.com/in/${faker.internet.username()}`,
      },
    },
  };
};
